import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Smartphone, 
  ChevronDown,
  Search,
  MessageCircle,
  Mail,
  Menu,
  X,
  Sparkles
} from 'lucide-react';

const FAQPage = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: t('faq.categories.all') },
    { id: 'general', name: t('faq.categories.general') },
    { id: 'technical', name: t('faq.categories.technical') },
    { id: 'pricing', name: t('faq.categories.pricing') },
    { id: 'business', name: t('faq.categories.business') },
    { id: 'gifts', name: t('faq.categories.gifts') }
  ];

  const faqs = [
    {
      category: 'general',
      question: 'What is ScanIt2Go?',
      answer: 'ScanIt2Go is a platform that lets you link digital content (text, photos, videos) to physical objects using NFC tags and QR codes. When someone taps or scans, they instantly see your content on their phone - no app required.'
    },
    {
      category: 'general',
      question: 'How does NFC technology work?',
      answer: 'NFC (Near Field Communication) is a wireless technology that allows data transfer between devices at close range. When someone holds their smartphone near an NFC tag, it automatically opens a web page with your content. It works with most modern smartphones without any app installation.'
    },
    {
      category: 'general',
      question: 'Do I need a special app to use ScanIt2Go?',
      answer: 'No! That\'s the beauty of our solution. Anyone can tap an NFC tag or scan a QR code using their smartphone\'s built-in capabilities. The content opens directly in their web browser - no downloads required.'
    },
    {
      category: 'technical',
      question: 'What types of content can I upload?',
      answer: 'You can upload text messages, photos, videos, and audio files. Our platform supports common formats including JPG, PNG, MP4, MOV, MP3, and more. Files are automatically optimized for fast loading on mobile devices.'
    },
    {
      category: 'technical',
      question: 'Is there a file size limit?',
      answer: 'Yes, individual files can be up to 100MB. However, we recommend keeping files under 50MB for the best user experience. Our system automatically compresses images and videos to ensure fast loading times without sacrificing quality.'
    },
    {
      category: 'technical',
      question: 'Can I update content after it\'s been uploaded?',
      answer: 'For security, once content is saved to a tag, it becomes locked. To update content, you\'ll need admin access to reset the tag first. This prevents unauthorized modifications to your content.'
    },
    {
      category: 'technical',
      question: 'What analytics are available?',
      answer: 'Our analytics dashboard shows you when and where your tags are scanned, what devices are used, geographic distribution of scans, and engagement trends over time. Business plans include advanced analytics with detailed reports.'
    },
    {
      category: 'pricing',
      question: 'Is there a free trial?',
      answer: 'Yes! We offer a free trial that lets you experience the full platform. You can create tags, upload content, and access analytics. No credit card required to start.'
    },
    {
      category: 'pricing',
      question: 'What\'s included in each plan?',
      answer: 'Our Starter plan ($39/month) includes up to 100 tags with basic analytics. Professional ($99/month) gives you 1,000 tags, advanced analytics, API access, and priority support. Enterprise plans include unlimited tags, white-labeling, and dedicated support.'
    },
    {
      category: 'pricing',
      question: 'Can I change plans later?',
      answer: 'Absolutely! You can upgrade or downgrade your plan at any time. When you upgrade, you get immediate access to new features. When you downgrade, the change takes effect at your next billing cycle.'
    },
    {
      category: 'business',
      question: 'What is white-labeling?',
      answer: 'White-labeling lets you brand the entire platform with your company\'s identity. Your customers see your logo, colors, and domain - not ScanIt2Go. Perfect for agencies and businesses who want to offer NFC solutions under their own brand.'
    },
    {
      category: 'business',
      question: 'Can I manage multiple clients?',
      answer: 'Yes! Our multi-tenant architecture allows you to manage separate tag collections for different clients or campaigns. Each tenant can have its own branding, user access, and analytics.'
    },
    {
      category: 'business',
      question: 'Do you offer API access?',
      answer: 'Yes, Professional and Enterprise plans include full API access. You can integrate tag creation, content management, and analytics into your existing systems and workflows.'
    },
    {
      category: 'business',
      question: 'How do batch uploads work?',
      answer: 'With batch uploads, you can create hundreds of tags at once using a CSV file. Simply upload your file with tag codes and optional preset content, and we\'ll generate all the tags automatically. Perfect for large-scale deployments.'
    },
    {
      category: 'gifts',
      question: 'What is the Gifts & Awards product line?',
      answer: 'Gifts & Awards is our consumer-focused solution for adding personal messages to physical gifts. Imagine a birthday card that plays a video message, or a trophy that replays the winning moment. We provide the NFC tags - you add the magic.'
    },
    {
      category: 'gifts',
      question: 'How do gift recipients view the message?',
      answer: 'When the recipient taps their phone on the gift or scans the QR code, they\'re taken directly to your personalized message. It works on any smartphone without installing an app.'
    },
    {
      category: 'gifts',
      question: 'Can I preview my message before sending the gift?',
      answer: 'Yes! After uploading your content, you can preview exactly what the recipient will see. Make sure everything looks perfect before sending that special gift.'
    },
    {
      category: 'gifts',
      question: 'What happens if someone loses their upload link?',
      answer: 'Contact our support team with your order details, and we can help you retrieve your link or reset the tag if needed. We keep records to ensure you never lose access to your memories.'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">ScanIt2Go</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.features')}</Link>
              <Link to="/#solutions" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.solutions')}</Link>
              <Link to="/#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.pricing')}</Link>
              <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.about')}</Link>
              <Link to="/faq" className="text-indigo-600 font-medium">{t('marketing.nav.faq')}</Link>
            </div>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-gray-900 transition-colors">{t('marketing.nav.login')}</Link>
              <Link to="/#contact" className="px-5 py-2.5 bg-indigo-600 text-white rounded-full font-medium hover:bg-indigo-700 transition-colors">
                {t('marketing.nav.getStarted')}
              </Link>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <div className="px-6 py-4 space-y-4">
              <Link to="/#features" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.features')}</Link>
              <Link to="/#solutions" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.solutions')}</Link>
              <Link to="/#pricing" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.pricing')}</Link>
              <Link to="/about" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.about')}</Link>
              <Link to="/faq" className="block text-indigo-600 font-medium">{t('marketing.nav.faq')}</Link>
              <hr className="border-gray-100" />
              <Link to="/admin" className="block text-gray-600 hover:text-gray-900">{t('marketing.nav.login')}</Link>
              <Link to="/#contact" className="block w-full text-center px-5 py-2.5 bg-indigo-600 text-white rounded-full font-medium">
                {t('marketing.nav.getStarted')}
              </Link>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full text-indigo-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              {t('faq.badge')}
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              {t('faq.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              {t('faq.subtitle')}
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('faq.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-2xl border border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-lg"
                data-testid="faq-search"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="px-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`category-${category.id}`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No questions found matching your search.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-indigo-200 transition-colors"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                    data-testid={`faq-question-${index}`}
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                        openIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('faq.stillHaveQuestions')}</h2>
              <p className="text-gray-600">{t('faq.stillHaveQuestionsDesc')}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <a
                href="mailto:andy@scanit2go.com"
                className="flex items-center gap-4 bg-white rounded-2xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('faq.emailUs')}</h3>
                  <p className="text-sm text-gray-500">andy@scanit2go.com</p>
                </div>
              </a>

              <Link
                to="/#contact"
                className="flex items-center gap-4 bg-white rounded-2xl p-6 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('faq.contactSupport')}</h3>
                  <p className="text-sm text-gray-500">{t('marketing.contact.sendMessage')}</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl">ScanIt2Go</span>
              </div>
              <p className="text-gray-400">
                Bridge physical to digital. Create unforgettable experiences.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/#features" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/#pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/#solutions" className="hover:text-white transition-colors">Solutions</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>andy@scanit2go.com</li>
                <li>+44 7829 710 543</li>
                <li>www.scanit2go.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400">&copy; 2026 ScanIt2Go. All rights reserved.</p>
            <div className="flex items-center gap-4 text-gray-400">
              <a href="#" className="hover:text-white transition-colors">Twitter</a>
              <a href="#" className="hover:text-white transition-colors">LinkedIn</a>
              <a href="#" className="hover:text-white transition-colors">Instagram</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
